import { z } from "zod";
import { creditCertaintySchema } from "./states";

/**
 * One append-only ledger entry. Kinds are reserve, consume, release, purchase, grant, refund,
 * unknown, and metered. A refund is a reversal of one earlier confirmed purchase: a negative
 * amount with confirmed certainty. A metered entry records BYOK usage the customer pays for
 * directly: it carries no credit amount, only a note with the token counts, so spend is
 * reported without credits being consumed. There is no update and no delete: corrections are
 * new entries.
 */
export const ledgerEntryKinds = [
  "reserve",
  "consume",
  "release",
  "purchase",
  "grant",
  "refund",
  "unknown",
  "metered",
  /**
   * Records that a project owner asserted what an unresolved spend amounted to. The gate that
   * blocks starts while spend is unknown accepts a run once a row of this kind names it.
   */
  "resolution",
] as const;
export const ledgerEntryKindSchema = z.enum(ledgerEntryKinds);
export type LedgerEntryKind = z.infer<typeof ledgerEntryKindSchema>;

export const ledgerEntrySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  kind: ledgerEntryKindSchema,
  amount: z.number().int(),
  certainty: creditCertaintySchema,
  runId: z.uuid().nullable(),
  note: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type LedgerEntry = z.infer<typeof ledgerEntrySchema>;

/** The ledger filters, as the Usage board tabs them: all, purchases, usage. */
export const ledgerFilters = ["all", "purchases", "usage"] as const;
export const ledgerFilterSchema = z.enum(ledgerFilters);
export type LedgerFilter = z.infer<typeof ledgerFilterSchema>;

export const ledgerQuerySchema = z.object({
  kind: ledgerFilterSchema.optional().default("all"),
  page: z.coerce.number().int().min(1).optional().default(1),
  pageSize: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type LedgerQuery = z.infer<typeof ledgerQuerySchema>;

/** A project owner asserting what a run's unresolved spend amounted to. */
export const resolveUnknownSpendSchema = z.object({
  runId: z.uuid(),
  amount: z.number().int().min(0).max(1_000_000),
  note: z.string().trim().min(1).max(500).optional(),
});
export type ResolveUnknownSpend = z.infer<typeof resolveUnknownSpendSchema>;

export const resolveUnknownSpendResponseSchema = z.object({ resolved: z.literal(true) });
export type ResolveUnknownSpendResponse = z.infer<typeof resolveUnknownSpendResponseSchema>;

export const ledgerResponseSchema = z.object({
  entries: z.array(ledgerEntrySchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
});
export type LedgerResponse = z.infer<typeof ledgerResponseSchema>;

/**
 * `GET /api/v1/projects/:id/wallet`. One wallet per project: the available balance, the monthly
 * limit with its reset date, and the four certainty totals, always labelled. Unknown stays
 * visible, never zero.
 */
export const walletSchema = z.object({
  projectId: z.uuid(),
  available: z.number().int().min(0),
  monthlyLimit: z.number().int().min(0),
  monthlyUsed: z.number().int().min(0),
  resetsAt: z.iso.datetime(),
  perRunCap: z.number().int().min(1),
  confirmed: z.number().int().min(0),
  reserved: z.number().int().min(0),
  estimated: z.number().int().min(0),
  unknownCount: z.number().int().min(0),
});
export type Wallet = z.infer<typeof walletSchema>;

/**
 * This month's spend grouped by flow, as the Usage board tabulates it. Flows have no tables yet,
 * so the name is the frozen flow version on the run; a null version reads as unlabelled rather
 * than guessed.
 */
export const flowSpendSchema = z.object({
  flowVersion: z.string().nullable(),
  credits: z.number().int().min(0),
});
export type FlowSpend = z.infer<typeof flowSpendSchema>;

export const flowSpendListSchema = z.array(flowSpendSchema);
export type FlowSpendList = z.infer<typeof flowSpendListSchema>;

/**
 * The monthly spending limit and the per-run cap, as billing settings own them (task 13 reads
 * them here too). Kept here so the wallet and the settings page cannot drift.
 */
export const DEFAULT_MONTHLY_LIMIT = 60_000;
export const DEFAULT_PER_RUN_CAP = 2_500;
